import { useCallback } from "react";
import { message } from "antd";
import Papa from "papaparse";
import { saveAs } from "file-saver";
import { useTranslation } from "react-i18next";
import { getCustomListCSV } from "src/services/AudienceListAPIs";
import type { CustomList } from "../../EngagementInterfaces";

type CsvRow = Record<string, unknown>;

const EXPORT_MESSAGE_KEY = "export";
const CSV_MIME_TYPE = "text/csv;charset=utf-8;";

const fetchListRows = async (listId: number): Promise<CsvRow[]> => {
  const blob = await getCustomListCSV(listId);
  const text = await blob.text();
  return Papa.parse<CsvRow>(text, { header: true }).data;
};

const saveCsv = (rows: CsvRow[], fileName: string) => {
  saveAs(new Blob([Papa.unparse(rows)], { type: CSV_MIME_TYPE }), fileName);
};

export const useAudienceExport = (lists: CustomList[]) => {
  const { t } = useTranslation();

  const getListName = useCallback(
    (listId: number, fallback: string) =>
      lists.find(list => list.custom_list_id === listId)?.list_name || fallback,
    [lists],
  );

  const exportSingleList = useCallback(
    async (listId: number) => {
      message.loading({ content: t("audienceOverview.exportingList"), key: EXPORT_MESSAGE_KEY });
      try {
        const rows = await fetchListRows(listId);
        saveCsv(rows, `${getListName(listId, t("audienceOverview.audienceListFallback"))}.csv`);
        message.success({
          content: t("audienceOverview.listExportedSuccess"),
          key: EXPORT_MESSAGE_KEY,
        });
      } catch (error) {
        // eslint-disable-next-line no-console -- intentional error logging
        console.error("Export failed:", error);
        message.error({ content: t("audienceOverview.failedToExportList"), key: EXPORT_MESSAGE_KEY });
      }
    },
    [getListName, t],
  );

  const exportCombinedLists = useCallback(
    async (listIds: number[]) => {
      message.loading({
        content: t("audienceOverview.exportingCombinedData"),
        key: EXPORT_MESSAGE_KEY,
      });
      try {
        const combinedRows: CsvRow[] = [];
        for (const listId of listIds) {
          const listName = getListName(listId, "");
          const rows = await fetchListRows(listId);
          combinedRows.push(...rows.map(row => ({ ...row, __ListName__: listName })));
        }
        const firstName = getListName(listIds[0], t("audienceOverview.audienceDataFallback"));
        const fileName =
          listIds.length > 1
            ? `${firstName}_and_${listIds.length - 1}_more_combined.csv`
            : `${firstName}.csv`;
        saveCsv(combinedRows, fileName);
        message.success({
          content: t("audienceOverview.combinedExportSuccess"),
          key: EXPORT_MESSAGE_KEY,
        });
      } catch (error) {
        // eslint-disable-next-line no-console -- intentional error logging
        console.error("Export failed:", error);
        message.error({ content: t("audienceOverview.failedToExportData"), key: EXPORT_MESSAGE_KEY });
      }
    },
    [getListName, t],
  );

  const exportSeparateLists = useCallback(
    async (listIds: number[]) => {
      message.loading({
        content: t("audienceOverview.exportingListsSeparately"),
        key: EXPORT_MESSAGE_KEY,
      });
      try {
        for (const listId of listIds) {
          const rows = await fetchListRows(listId);
          saveCsv(rows, `${getListName(listId, t("audienceOverview.audienceListFallback"))}.csv`);
        }
        message.success({
          content: t("audienceOverview.separateExportsCompleted"),
          key: EXPORT_MESSAGE_KEY,
        });
      } catch (error) {
        // eslint-disable-next-line no-console -- intentional error logging
        console.error("Export failed:", error);
        message.error({ content: t("audienceOverview.failedToExportLists"), key: EXPORT_MESSAGE_KEY });
      }
    },
    [getListName, t],
  );

  return { exportSingleList, exportCombinedLists, exportSeparateLists };
};
